"use client"

import useEmblaCarousel from "embla-carousel-react"
import React, { useCallback, useEffect, useState } from "react"

import ArrowCarouselDark from "../../../public/icons/arrow-carousel-dark.svg"
import ArrowCarousel from "../../../public/icons/arrow-carousel.svg"

import PageGrid from "./PageGrid"

interface FullWidthCarouselProps {
  children: React.ReactNode
  className?: string
  navigationClassName?: string
  showNavigation?: boolean
  emblaOptions?: any
  theme?: "light" | "dark"
  onPrevClick?: () => void
  onNextClick?: () => void
}

const FullWidthCarousel: React.FC<FullWidthCarouselProps> = ({
  children,
  className = "",
  navigationClassName = "",
  showNavigation = true,
  emblaOptions = {
    loop: false,
    align: "center",
    skipSnaps: false,
    dragFree: false,
    duration: 20,
    slidesToScroll: 1,
  },
  theme = "light",
  onPrevClick,
  onNextClick,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
    onPrevClick?.()
  }, [emblaApi, onPrevClick])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
    onNextClick?.()
  }, [emblaApi, onNextClick])

  const onSelect = useCallback((api: any) => {
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on("reInit", onSelect)
    emblaApi.on("select", onSelect)
  }, [emblaApi, onSelect])

  const ArrowIcon = theme === "dark" ? ArrowCarouselDark : ArrowCarousel
  const buttonClasses =
    "disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-50 cursor-pointer relative z-10"

  return (
    <>
      <div className={`embla ${className}`} ref={emblaRef}>
        <div className="embla__container">{children}</div>
      </div>

      {showNavigation && (canScrollPrev || canScrollNext) && (
        <PageGrid className={navigationClassName} isGrid={false}>
          <button
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className={buttonClasses}
            type="button"
            aria-label="Previous slide"
          >
            <ArrowIcon className="mr-10 transform -rotate-180 h-14 w-14" />
          </button>
          <button
            onClick={scrollNext}
            disabled={!canScrollNext}
            className={buttonClasses}
            type="button"
            aria-label="Next slide"
          >
            <ArrowIcon className="h-14 w-14" />
          </button>
        </PageGrid>
      )}
    </>
  )
}

export default FullWidthCarousel
